import React, { useCallback, useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { FilterTeacherBySchool } from "../../store/actions/user.action";
import { base_url, FUHSD_schools } from "../../components/Transformation/Transformations";
import ActiveLingoCard from "../../components/Cards/ActiveLingoCard";
import Pagination from "../../components/Pagination/Pagination";
import Loader from "../../components/Loader/Loader";
import TimeBlockFilter from "../../components/Filter/TimeBlockFilter";
import { isLeaderAvailableInBlock } from "../../utils/availabilityFilter";

function FUHSDSchools({ 
  token, 
  parsedUserProfile, 
  activeButton, 
  setActiveButton,
  setPage,
  buttonIndexOffset,
  page
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchSchool, setSearchSchool] = useState("");
  const [loading, setLoading] = React.useState(false);
  const [fetchingAvailabilities, setFetchingAvailabilities] = useState(false);
  const [availabilitiesMap, setAvailabilitiesMap] = useState({});
  const [selectedTimeBlock, setSelectedTimeBlock] = useState("all");
  const allShortLeaders = useSelector((state) => state.user.allShortLeaders);

  const fetchTeachers = useCallback(
    async (currentPage, searchSchool) => {
      setLoading(true);
      await dispatch(
        FilterTeacherBySchool(token, 'FUHSD', searchSchool, currentPage)
      );
      setLoading(false);
    },
    [token, dispatch]
  );

  useEffect(() => {
    if (searchSchool || selectedTimeBlock !== "all") {
      fetchTeachers(page, searchSchool);
    }
  }, [token, page, fetchTeachers, searchSchool, selectedTimeBlock]);

  useEffect(() => {
    setPage(1);
  }, [searchSchool, setPage]);

  useEffect(() => {
    if (!allShortLeaders?.results || selectedTimeBlock === "all") return;

    const leadersToFetch = allShortLeaders.results.filter(
      (leader) =>
        leader.id &&
        !availabilitiesMap[leader.id] &&
        !leader.owner?.availabilities &&
        !leader.availabilities
    );

    if (leadersToFetch.length === 0) return;

    let isMounted = true;
    setFetchingAvailabilities(true);

    Promise.all(
      leadersToFetch.map((leader) =>
        axios
          .get(`${base_url}/clubusers/leader-profiles/${leader.id}/`)
          .then((res) => ({
            id: leader.id,
            availabilities:
              res.data?.owner?.availabilities ||
              res.data?.availability_time ||
              [],
          }))
          .catch(() => ({ id: leader.id, availabilities: [] }))
      )
    ).then((fetchedResults) => {
      if (!isMounted) return;
      setAvailabilitiesMap((prev) => {
        const next = { ...prev };
        fetchedResults.forEach(({ id, availabilities }) => {
          next[id] = availabilities;
        });
        return next;
      });
      setFetchingAvailabilities(false);
    });

    return () => {
      isMounted = false;
    };
  }, [allShortLeaders, selectedTimeBlock, availabilitiesMap]);

  const filteredLeaders = useMemo(() => {
    if (!allShortLeaders?.results) return [];
    return allShortLeaders.results.filter((leader) =>
      isLeaderAvailableInBlock(
        leader,
        selectedTimeBlock,
        parsedUserProfile?.timezone,
        availabilitiesMap[leader.id]
      )
    );
  }, [allShortLeaders, selectedTimeBlock, parsedUserProfile?.timezone, availabilitiesMap]);

  const numberOfPages = Math.ceil((filteredLeaders?.length || 0) / 10) || 0;

  const handleBtn = (buttonId, school) => {
    if (activeButton === buttonId) {
      setActiveButton(null);
      setSearchSchool("");
    } else {
      setActiveButton(buttonId);
      setSearchSchool(school);
    }
    setPage(1);
  };

  const handleSchoolClick = (school, index) => {
    const buttonId = index + buttonIndexOffset;
    handleBtn(buttonId, school.value);
  };

  // Don't show FUHSD schools if user is logged in and not from FUHSD
  if (token && parsedUserProfile?.affiliation !== 'FUHSD') {
    return null;
  }

  const showResults = Boolean(searchSchool || selectedTimeBlock !== "all");

  return (
    <div className="flex flex-col items-center py-5 gap-2 justify-center">
      <TimeBlockFilter
        selectedBlock={selectedTimeBlock}
        setSelectedBlock={setSelectedTimeBlock}
      />

      <div className="bg-white border border-[var(--border)] rounded-full p-2 flex flex-wrap gap-2 justify-center max-w-4xl mx-auto mt-4">
        {FUHSD_schools.map((school, index) => (
          <button
            key={school.value}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              activeButton === index + buttonIndexOffset
                ? 'bg-[var(--brand)] text-white'
                : 'bg-transparent text-[var(--muted)] hover:bg-[var(--brand)] hover:text-white'
            }`}
            onClick={() => handleSchoolClick(school, index)}
          >
            {school.label}
          </button>
        ))}
      </div>
      <p className="text-xs sm:text-sm text-gray-600 italic text-center mt-2 px-4">
        {t("leader_page.eld_note")}
      </p>

      {showResults && (
        <>
          <div className="pb-[2rem] mt-6 w-full">
            {loading || fetchingAvailabilities ? (
              <Loader />
            ) : filteredLeaders && filteredLeaders.length > 0 ? (
              <div className="grid sm:grid-cols-1 lg:grid-cols-2 md:gap-10 gap-5 max-w-[1100px] mx-auto my-0 w-full mb-4">
                {filteredLeaders.map((leader) => (
                  <ActiveLingoCard
                    id={leader.id}
                    key={leader.id}
                    image={leader.owner.image_thumbnail}
                    name={leader.owner.first_name + " " + leader.owner.last_name}
                    teaching_language={leader.teaching_language}
                    reviewCount={leader?.owner?.review_count}
                    reviewScore={leader?.owner?.review_score}
                    credit_adjustment_rate={leader?.credit_adjustment_rate}
                    also_speak={leader?.owner?.also_speak}
                  />
                ))}
              </div>
            ) : (
              <p className="font-semibold text-xl text-center">
                {t("leader_page.no_leader_text")}
              </p>
            )}
          </div>
          <div className="flex justify-center">
            <Pagination pageNumber={numberOfPages} setPage={setPage} page={page} />
          </div>
        </>
      )}
    </div>
  );
}

export default FUHSDSchools;


